/**
 * Approvals Queue - Pending approval items
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CheckSquare, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : '—';

const ENTITY_LABELS = {
  invoice: 'Invoice',
  bill: 'Bill',
  commission_batch: 'Commission Batch',
  payroll_run: 'Payroll',
  journal_entry: 'Journal Entry',
  expense: 'Expense'
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

const PriorityChip = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
};

export default function ApprovalsQueue({ user }) {
  const [approvals, setApprovals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: '', skip: 0, limit: 25 });

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      params.append('skip', filters.skip);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/finance/hub/approvals?${params}`);
      setApprovals(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const handleApprove = async (approval) => {
    if (!window.confirm(`Approve ${ENTITY_LABELS[approval.entity_type] || approval.entity_type} ${approval.entity_number}?`)) return;
    
    try {
      // Determine the correct endpoint based on entity type
      let endpoint;
      if (approval.entity_type === 'commission_batch') {
        endpoint = `${API}/finance/commissions/batches/${approval.entity_id}/approve`;
      } else {
        endpoint = `${API}/finance/approvals/${approval.id}/approve`;
      }
      
      await axios.post(endpoint, { comments: 'Approved via Finance Hub' });
      toast.success('Approved successfully');
      fetchApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (approval) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    
    try {
      let endpoint;
      if (approval.entity_type === 'commission_batch') {
        endpoint = `${API}/finance/commissions/batches/${approval.entity_id}/reject`;
      } else {
        endpoint = `${API}/finance/approvals/${approval.id}/reject`;
      }
      
      await axios.post(endpoint, { reason });
      toast.success('Rejected');
      fetchApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  return (
    <Card data-testid="approvals-queue">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-amber-500" />
          Pending Approvals ({total})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v === 'all' ? '' : v, skip: 0 })}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="bill">Bills</SelectItem>
              <SelectItem value="commission_batch">Commission Batches</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchApprovals}>Refresh</Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : approvals.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Submitted By</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Step</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvals.map((apr) => (
                  <TableRow key={apr.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <TableCell className="font-medium">
                      {ENTITY_LABELS[apr.entity_type] || apr.entity_type}
                    </TableCell>
                    <TableCell>{apr.entity_number || apr.entity_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(apr.amount)}</TableCell>
                    <TableCell className="text-slate-500">{apr.submitter_name || '—'}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(apr.submitted_at)}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm">
                        <Clock className="h-3 w-3" />
                        {apr.current_step}/{apr.total_steps}
                      </span>
                    </TableCell>
                    <TableCell><PriorityChip priority={apr.priority} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApprove(apr)} title="Approve">
                          <CheckCircle className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(apr)} title="Reject">
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckSquare className="h-10 w-10 mx-auto text-green-400 mb-2" />
            <p className="text-slate-500">No pending approvals</p>
            <p className="text-sm text-slate-400 mt-1">All caught up!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
